import React, { useEffect, useState } from 'react';
import axiosInstance from '../../axios/axiosInstance';

const statusStyle = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'paid') return { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' };
    if (s === 'failed') return { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' };
    if (s === 'pending') return { bg: '#fffbeb', color: '#b45309', border: '#fcd34d' };
    return { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' };
};

const AdminBillingHistory = () => {
    const [rows, setRows] = useState([]);
    const [firms, setFirms] = useState([]);
    const [firmId, setFirmId] = useState('');
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const load = async (filterFirmId) => {
        setLoading(true);
        try {
            const params = { take: 200 };
            if (filterFirmId) params.firmId = filterFirmId;
            const res = await axiosInstance.get('/billing/admin/history', { params });
            setRows(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setRows([]);
            showToast(err?.response?.data?.message || 'Failed to load billing history.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const firmsRes = await axiosInstance.get('/firms');
                setFirms(Array.isArray(firmsRes.data) ? firmsRes.data : []);
            } catch { /* ignore */ }
            await load('');
        })();
    }, []);

    const money = (n) => `$${Number(n || 0).toFixed(2)}`;

    return (
        <div>
            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 24, zIndex: 9999,
                    background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
                    color: toast.type === 'error' ? '#dc2626' : '#16a34a',
                    borderRadius: 10, padding: '12px 20px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 14, fontWeight: 500,
                }}>
                    {toast.type === 'error' ? '✗ ' : '✓ '}{toast.msg}
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                <div>
                    <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a3a5c', margin: 0 }}>Billing Charges</h1>
                    <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 14 }}>
                        Charge history across reseller firms (from monthly charge runs).
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select
                        value={firmId}
                        onChange={(e) => {
                            const v = e.target.value;
                            setFirmId(v);
                            load(v);
                        }}
                        style={{
                            padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
                            fontSize: 13, minWidth: 220, background: '#fff',
                        }}
                    >
                        <option value="">All firms</option>
                        {firms.filter(f => f.type !== 'platform').map(f => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={() => load(firmId)}
                        style={{
                            background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
                            padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        }}
                    >
                        Refresh
                    </button>
                </div>
            </div>

            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
                ) : rows.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                        No billing cycles yet. Run monthly charges from Firms or Packages.
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    {['Firm', 'Period', 'Companies', 'Employees', 'Contractors', 'Pro-rate', 'Total', 'Status', 'Stripe PI'].map(h => (
                                        <th key={h} style={{
                                            padding: '10px 12px', textAlign: 'left', fontSize: 11,
                                            fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const st = statusStyle(r.status);
                                    const pro = Number(r.proRatedAmount || 0);
                                    return (
                                        <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '12px', fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                                                {r.firmName}
                                                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>Id {r.firmId}</div>
                                            </td>
                                            <td style={{ padding: '12px', fontSize: 13, color: '#334155' }}>
                                                {new Date(r.periodStart).toLocaleDateString()} – {new Date(r.periodEnd).toLocaleDateString()}
                                            </td>
                                            <td style={{ padding: '12px', fontSize: 13 }}>{r.companyCount}</td>
                                            <td style={{ padding: '12px', fontSize: 13 }}>{r.employeeCount}</td>
                                            <td style={{ padding: '12px', fontSize: 13 }}>{r.contractorCount}</td>
                                            <td style={{
                                                padding: '12px', fontSize: 13,
                                                color: pro > 0 ? '#0f766e' : '#94a3b8',
                                            }} title="Company mid-month discount">
                                                {pro > 0 ? `−${money(pro)}` : '—'}
                                            </td>
                                            <td style={{ padding: '12px', fontSize: 13, fontWeight: 700, color: '#1B75F0' }}>
                                                {money(r.totalAmount)}
                                            </td>
                                            <td style={{ padding: '12px' }}>
                                                <span style={{
                                                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                                                    background: st.bg, color: st.color, border: `1px solid ${st.border}`,
                                                }} title={r.errorMessage || ''}>
                                                    {r.status}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px', fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>
                                                {r.stripePaymentIntentId
                                                    ? String(r.stripePaymentIntentId).slice(0, 18) + '…'
                                                    : '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminBillingHistory;
